/**
 * Mapeo columna(BD) <-> campo(data del formulario) para Declaración de Renta
 * F-22, AT 2025, Régimen 14D3 (Pro PYME).
 *
 * Vive junto a formulas.js/initialState.js (mismo módulo, mismo año+régimen)
 * porque describe la forma de ESTE módulo — lo usan tanto el backend
 * (src/services/declaracionRenta/schemas/2025_14d3.js re-exporta este
 * archivo) como el frontend (RentaSavedView, vía el registry, para
 * reconstruir `data` a partir de una fila guardada sin duplicar el mapeo).
 */
const formulas = require('./formulas');
const { createInitialState } = require('./initialState');
const { buildR17_R21_Html } = require('./pdfExport');

const TABLE_PROPUESTO = 'renta_2025_14d3';
const TABLE_PAGADO = 'renta_2025_14d3_pagado';

// Campos de entrada: 1:1 con initialState.js (antecedentes.*, remanentesAnteriores.*, creditoIPE.*)
const INPUT_FIELDS = [
  { column: 'ventas_no_relacionadas_neto', path: 'antecedentes.ventasNoRelacionadasNeto', type: 'number' },
  { column: 'ventas_no_relacionadas_no_percibido', path: 'antecedentes.ventasNoRelacionadasNoPercibido', type: 'number' },
  { column: 'ventas_no_relacionadas_percibido', path: 'antecedentes.ventasNoRelacionadasPercibido', type: 'number' },
  { column: 'ventas_relacionadas_pro_pyme_neto', path: 'antecedentes.ventasRelacionadasProPymeNeto', type: 'number' },
  { column: 'ventas_relacionadas_pro_pyme_no_percibido', path: 'antecedentes.ventasRelacionadasProPymeNoPercibido', type: 'number' },
  { column: 'ventas_relacionadas_pro_pyme_percibido', path: 'antecedentes.ventasRelacionadasProPymePercibido', type: 'number' },
  { column: 'ventas_relacionadas_14a_neto', path: 'antecedentes.ventasRelacionadas14ANeto', type: 'number' },
  { column: 'ventas_relacionadas_14a_no_percibido', path: 'antecedentes.ventasRelacionadas14ANoPercibido', type: 'number' },
  { column: 'ventas_relacionadas_14a_percibido', path: 'antecedentes.ventasRelacionadas14APercibido', type: 'number' },
  { column: 'cobro_factura_2023_neto', path: 'antecedentes.cobroFactura2023Neto', type: 'number' },
  { column: 'cobro_factura_2023_no_percibido', path: 'antecedentes.cobroFactura2023NoPercibido', type: 'number' },
  { column: 'cobro_factura_2023_percibido', path: 'antecedentes.cobroFactura2023Percibido', type: 'number' },
  { column: 'ingresos_venta_computador', path: 'antecedentes.ingresosVentaComputador', type: 'number' },
  { column: 'credito_banco', path: 'antecedentes.creditoBanco', type: 'number' },
  { column: 'dividendo1_percibido', path: 'antecedentes.dividendo1Percibido', type: 'number' },
  { column: 'dividendo1_factor', path: 'antecedentes.dividendo1Factor', type: 'number' },
  { column: 'dividendo1_credito', path: 'antecedentes.dividendo1Credito', type: 'number' },
  { column: 'dividendo1_regimen', path: 'antecedentes.dividendo1Regimen', type: 'string' },
  { column: 'dividendo2_percibido', path: 'antecedentes.dividendo2Percibido', type: 'number' },
  { column: 'dividendo2_factor', path: 'antecedentes.dividendo2Factor', type: 'number' },
  { column: 'dividendo2_credito', path: 'antecedentes.dividendo2Credito', type: 'number' },
  { column: 'dividendo2_regimen', path: 'antecedentes.dividendo2Regimen', type: 'string' },
  { column: 'dividendo3_percibido', path: 'antecedentes.dividendo3Percibido', type: 'number' },
  { column: 'dividendo3_factor', path: 'antecedentes.dividendo3Factor', type: 'number' },
  { column: 'dividendo3_credito', path: 'antecedentes.dividendo3Credito', type: 'number' },
  { column: 'dividendo3_regimen', path: 'antecedentes.dividendo3Regimen', type: 'string' },
  { column: 'dividendo4_percibido', path: 'antecedentes.dividendo4Percibido', type: 'number' },
  { column: 'dividendo_inr', path: 'antecedentes.dividendoINR', type: 'number' },
  { column: 'dividendo_stut', path: 'antecedentes.dividendoSTUT', type: 'number' },
  { column: 'total_dividendos_percibido', path: 'antecedentes.totalDividendosPercibido', type: 'number' },
  { column: 'compras_neto', path: 'antecedentes.comprasNeto', type: 'number' },
  { column: 'compras_pagado', path: 'antecedentes.comprasPagado', type: 'number' },
  { column: 'compra_camioneta_neto', path: 'antecedentes.compraCamionetaNeto', type: 'number' },
  { column: 'compra_camioneta_pagado', path: 'antecedentes.compraCamionetaPagado', type: 'number' },
  { column: 'remuneraciones_bruto', path: 'antecedentes.remuneracionesBruto', type: 'number' },
  { column: 'remuneraciones_pagado', path: 'antecedentes.remuneracionesPagado', type: 'number' },
  { column: 'remuneraciones_adeudado', path: 'antecedentes.remuneracionesAdeudado', type: 'number' },
  { column: 'honorarios_bruto', path: 'antecedentes.honorariosBruto', type: 'number' },
  { column: 'honorarios_adeudado', path: 'antecedentes.honorariosAdeudado', type: 'number' },
  { column: 'honorarios_pagados', path: 'antecedentes.honorariosPagados', type: 'number' },
  { column: 'gastos_generales', path: 'antecedentes.gastosGenerales', type: 'number' },
  { column: 'arriendos', path: 'antecedentes.arriendos', type: 'number' },
  { column: 'cotizaciones_dic_2023', path: 'antecedentes.cotizacionesDic2023', type: 'number' },
  { column: 'retenciones_iusc_dic_2023', path: 'antecedentes.retencionesIUSCDic2023', type: 'number' },
  { column: 'pago_proveedores_2023', path: 'antecedentes.pagoProveedores2023', type: 'number' },
  { column: 'amortizacion_capital', path: 'antecedentes.amortizacionCapital', type: 'number' },
  { column: 'amortizacion_interes', path: 'antecedentes.amortizacionInteres', type: 'number' },
  { column: 'pago_intereses_multas', path: 'antecedentes.pagoInteresesMultas', type: 'number' },
  { column: 'pago_idpc_at_2024', path: 'antecedentes.pagoIDPCAT2024', type: 'number' },
  { column: 'ppm_historico', path: 'antecedentes.ppmHistorico', type: 'number' },
  { column: 'reajuste_ppm_historico', path: 'antecedentes.reajustePPMHistorico', type: 'number' },
  { column: 'cpts_al_31_dic_2023', path: 'antecedentes.cptsAl31Dic2023', type: 'number' },
  { column: 'valor_uf', path: 'antecedentes.valorUF', type: 'number' },
  { column: 'tasa_idpc', path: 'antecedentes.tasaIDPC', type: 'number' },
  { column: 'remanente_rai', path: 'antecedentes.remanentesAnteriores.RAI', type: 'number' },
  { column: 'remanente_rex_rap', path: 'antecedentes.remanentesAnteriores.REX_RAP', type: 'number' },
  { column: 'remanente_rex_inr', path: 'antecedentes.remanentesAnteriores.REX_INR', type: 'number' },
  { column: 'remanente_sac_no_sujeto_sin_dev', path: 'antecedentes.remanentesAnteriores.SAC_noSujetoSinDev', type: 'number' },
  { column: 'remanente_sac_no_sujeto_con_dev', path: 'antecedentes.remanentesAnteriores.SAC_noSujetoConDev', type: 'number' },
  { column: 'remanente_sac_sujeto_con_dev', path: 'antecedentes.remanentesAnteriores.SAC_sujetoConDev', type: 'number' },
  { column: 'remanente_sac_ipe_sin_dev', path: 'antecedentes.remanentesAnteriores.SAC_IPE_sinDev', type: 'number' },
  { column: 'remanente_sac_2016_con_dev', path: 'antecedentes.remanentesAnteriores.SAC_2016_conDev', type: 'number' },
  { column: 'remanente_stut', path: 'antecedentes.remanentesAnteriores.STUT', type: 'number' },
  { column: 'remanente_credito_idpc_2016_con_dev', path: 'antecedentes.remanentesAnteriores.creditoIDPC2016ConDev', type: 'number' },
  { column: 'credito_ipe_dividendo_extranjero_usd', path: 'creditoIPE.dividendoExtranjeroUSD', type: 'number' },
  { column: 'credito_ipe_tipo_cambio_dolar', path: 'creditoIPE.tipoCambioDolar', type: 'number' },
  { column: 'credito_ipe_tasa_retencion', path: 'creditoIPE.tasaRetencion', type: 'number' },
  { column: 'credito_ipe_tasa_corporativo', path: 'creditoIPE.tasaCorporativo', type: 'number' },
  { column: 'credito_ipe_perdidas_relacionadas', path: 'creditoIPE.perdidasRelacionadas', type: 'number' },
  { column: 'credito_ipe_gastos_relacionados', path: 'creditoIPE.gastosRelacionados', type: 'number' },
];

// Códigos SII de salida: cada uno resuelto con la MISMA función que ya usan
// las secciones en pantalla (baseImponible en BaseImponibleSection, r17Fn/
// r18Fn/r19Fn en sections/R1*.js), para garantizar que lo guardado sea
// idéntico a lo que el usuario vería.
const OUTPUT_CODES = [
  { column: 'cod_36', fn: (d) => formulas.baseImponible.ppmActualizado(d) },
  { column: 'cod_85', fn: (d) => formulas.baseImponible.saldoAFavor(d) },
  { column: 'cod_87', fn: (d) => formulas.baseImponible.devolucionSolicitada(d) },
  { column: 'cod_305', fn: (d) => formulas.baseImponible.resultadoLiquidacionAnual(d) },
  // CPT: en 14D3 siempre 0 — Pro Pyme determina CPTS (código 1545), no CPT.
  { column: 'cod_645', fn: () => 0 },
  { column: 'cod_646', fn: () => 0 },
  { column: 'cod_1374', fn: (d) => formulas.r19.capitalAportado1374(d) },
  { column: 'cod_1379', fn: (d) => formulas.r19.creditoIPE(d) },
  { column: 'cod_1400', fn: (d) => formulas.r17.ingresosGiroPercibidos(d) },
  { column: 'cod_1401', fn: (d) => formulas.r17.rentasFuenteExtranjera(d) },
  { column: 'cod_1409', fn: (d) => formulas.r17.existenciasPagadas(d) },
  { column: 'cod_1410', fn: (d) => formulas.r17.totalIngresos(d) },
  { column: 'cod_1411', fn: (d) => formulas.r17.remuneracionesPagadas(d) },
  { column: 'cod_1412', fn: (d) => formulas.r17.honorariosPagados(d) },
  { column: 'cod_1413', fn: (d) => formulas.r17.activoFijoPagado(d) },
  { column: 'cod_1415', fn: (d) => formulas.r17.arriendosPagados(d) },
  { column: 'cod_1419', fn: (d) => formulas.r17.interesesPagados(d) },
  { column: 'cod_1422', fn: (d) => formulas.r17.partidasArt21(d) },
  { column: 'cod_1424', fn: (d) => formulas.r17.otrosGastos(d) },
  { column: 'cod_1430', fn: (d) => formulas.r17.totalEgresos(d) },
  { column: 'cod_1431', fn: (d) => formulas.r17.gastosRechazadosArt21(d) },
  { column: 'cod_1432', fn: (d) => formulas.r17.incentivoAhorro(d) },
  { column: 'cod_1440', fn: (d) => formulas.r17.biAfectaIDPC(d) },
  { column: 'cod_1445', fn: (d) => formulas.r19.cptInicial(d) },
  { column: 'cod_1479', fn: (d) => formulas.r19.retiros(d) },
  { column: 'cod_1484', fn: (d) => formulas.r20.remanenteSiguiente(d).RAI },
  { column: 'cod_1493', fn: (d) => formulas.r18.saldoREXPositivo(d) },
  { column: 'cod_1494', fn: (d) => formulas.r18.capitalAportado(d) },
  { column: 'cod_1500', fn: (d) => formulas.r18.rai(d) },
  { column: 'cod_1545', fn: (d) => formulas.r19.cptsPositivoFinal(d) },
  { column: 'cod_1564', fn: (d) => formulas.r21.remanenteSiguiente(d).SAC_noSujetoConDev },
  { column: 'cod_1587', fn: (d) => formulas.r17.ingresosRelacionados14A(d) },
  { column: 'cod_1588', fn: (d) => formulas.r17.otrosIngresosPercibidos(d) },
  { column: 'cod_1703', fn: (d) => formulas.r18.cptsPositivo(d) },
  { column: 'cod_1704', fn: (d) => formulas.r18.retirosHistoricos(d) },
  { column: 'cod_1705', fn: (d) => formulas.r19.biAfecta(d) },
  { column: 'cod_1708', fn: (d) => formulas.r19.partidasArt21(d) },
  { column: 'cod_1710', fn: (d) => formulas.r19.incentivoAhorro(d) },
  { column: 'cod_1720', fn: (d) => formulas.r18.subtotal(d) },
  { column: 'cod_1726', fn: (d) => formulas.r19.dividendosPercibidos(d) },
  { column: 'cod_1729', fn: (d) => formulas.r17.biAntesIncentivo(d) },
  { column: 'cod_1817', fn: (d) => formulas.r17.ingresosDevengadosPercibidos(d) },
  { column: 'cod_1818', fn: (d) => formulas.r17.gastosAdeudadosPagados(d) },
];

const getPath = (obj, p) => p.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);

const setPath = (obj, p, value) => {
  const keys = p.split('.');
  let cur = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    if (cur[keys[i]] == null) cur[keys[i]] = {};
    cur = cur[keys[i]];
  }
  cur[keys[keys.length - 1]] = value;
};

// Arma la fila completa (inputs + códigos calculados) a partir del `data`
// del formulario. El cálculo ocurre acá, una sola vez, al hacer "Calcular Renta".
function stateToRow(data) {
  const row = {};
  for (const f of INPUT_FIELDS) row[f.column] = getPath(data, f.path);
  row.socios = JSON.stringify(data.antecedentes.socios || []);
  row.retiros = JSON.stringify(data.antecedentes.retiros || []);

  for (const c of OUTPUT_CODES) row[c.column] = c.fn(data);

  row.rtre_registro = JSON.stringify({
    remanente: formulas.r20.remanente(data),
    reverso: formulas.r20.reverso(data),
    aumentoRAI: formulas.r20.aumentoRAI(data),
    aumentoINR: formulas.r20.aumentoINR(data),
    retiros: formulas.r20.retiros(data),
    remanenteSiguiente: formulas.r20.remanenteSiguiente(data),
  });
  row.sac_registro = JSON.stringify({
    remanente: formulas.r21.remanente(data),
    idpcBI: formulas.r21.idpcBI(data),
    idpcDividendo: formulas.r21.idpcDividendo(data),
    asignadoRetiros: formulas.r21.asignadoRetiros(data),
    gastosRechazados: formulas.r21.gastosRechazados(data),
    remanenteSiguiente: formulas.r21.remanenteSiguiente(data),
  });

  return row;
}

// Reconstruye el `data` del formulario (solo inputs) a partir de una fila
// guardada. Los valores calculados (`computed`) NO se leen de la fila: se
// vuelven a derivar llamando a `recalculate(data)` (función pura, barata,
// siempre consistente) — así no hace falta duplicar ~30 columnas más solo
// para la vista de solo lectura.
function rowToState(row) {
  const data = createInitialState();
  for (const f of INPUT_FIELDS) {
    const raw = row[f.column];
    const value = f.type === 'string' ? (raw ?? '') : Number(raw ?? 0);
    setPath(data, f.path, value);
  }
  data.antecedentes.socios = Array.isArray(row.socios) ? row.socios : (row.socios ? JSON.parse(row.socios) : []);
  data.antecedentes.retiros = Array.isArray(row.retiros) ? row.retiros : (row.retiros ? JSON.parse(row.retiros) : []);
  return data;
}

module.exports = {
  TABLE_PROPUESTO,
  TABLE_PAGADO,
  INPUT_FIELDS,
  OUTPUT_CODES,
  stateToRow,
  rowToState,
  recalculate: formulas.recalculate,
  buildPdfHtml: buildR17_R21_Html,
};
